import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Action, authenticate, CognitoState } from 'react-cognito';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import sample from 'lodash/sample';
import values from 'lodash/values';
import { buildAction } from '../../actions/helpers';
import { setRuntimeVariable } from '../../actions/runtime';
import {
  buildSearchParams,
  buildGlobalSearchParams,
  search,
  searchNext,
  clearTags,
} from '../../actions/search';
import { showSignup, showLogin, logout } from '../../actions/user';
import { setColors, clearColors } from '../../actions/ui';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  SET_MODAL,
  MODAL,
  SET_MODAL_VISIBLE,
  ONBOARDING_STEP,
  RESPONSIVE_SIZE,
  SEARCH_SOURCE,
} from '../../constants';
import { groupTags, allTags } from '../../lib/tags';
import { compose } from '../../lib/react';
import ClipLayout from '../../components/ClipLayout';
import SceneLayout from '../../components/SceneLayout';
import Header from '../../components/Header';
import { withFooterDrawer } from '../../components/Footer';
import SceneSearchResults from '../../components/SceneSearchResults';
import Style from '../../components/Style';

class Browse extends React.PureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    clip: PropTypes.object,
    sceneClips: PropTypes.arrayOf(PropTypes.object),
    activeTagIds: PropTypes.arrayOf(PropTypes.number),
    searchText: PropTypes.any,
    clearActiveTags: PropTypes.func,
    colors: PropTypes.object,
    getBackupSearchResults: PropTypes.func,
  };
  static defaultProps = {};
  static contextTypes = {
    store: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      // TODO: Perf: Separate this cluster.
      allResults: { fetching: true, result: EMPTY_OBJECT },
      backupResults: EMPTY_OBJECT,
      backupTag: EMPTY_OBJECT,
      matchingAll: true,
      globalQuery: EMPTY_OBJECT,
    };

    this.searchTimeout = undefined;
  }

  componentDidMount() {
    this.loadBrowseResults(false);
  }

  componentWillReceiveProps(nextProps) {
    const state = this.context.store.getState();
    const queryParams = buildGlobalSearchParams(state);
    if (!isEqual(this.state.globalQuery, queryParams)) {
      clearTimeout(this.searchTimeout);
      setTimeout(this.loadBrowseResults, 75);
    }
  }

  loadBrowseResults = (more, size) => {
    const state = this.context.store.getState();
    const filtering = state.search.text || state.search.tags.length > 0;
    let searchFunction = more ? searchNext : search;

    let source = state.search.source;
    if (filtering) {
      source = SEARCH_SOURCE.ALL_SCENES;
    }

    if (!filtering && source === SEARCH_SOURCE.FEATURED) {
      searchFunction = searchNext;
    }

    const queryParams = buildSearchParams(
      state.search.text,
      state.search.tags,
      source,
    );

    this.setState({
      allResults: { ...this.state.allResults, fetching: true },
      matchingAll: !filtering,
      globalQuery: queryParams,
    });

    return this.context.store
      .dispatch(
        searchFunction({ ...queryParams, size: size }, { iterations: 2 }),
      )
      .then(result => {
        // Should we load these particular results?
        if (!isEqual(this.state.globalQuery, queryParams)) {
          return;
        }

        const { clips, nextClips, ...others } = result;
        const merged = {
          ...others,
          clips: (clips || []).concat(nextClips || []),
        };
        this.setState({
          allResults: {
            ...this.state.allResults,
            fetching: false,
            result: merged,
          },
        });

        // If a search ever returns nothing we kick off the load of the backups.
        if (merged.clips.length < 1) {
          this.loadBackupResults();
        }
      });
  };

  loadBackupResults = () => {
    let tag = this.state.backupTag;
    if (tag.id === undefined) {
      tag = sample(groupTags(values(allTags()))['Category']['undefined']);
    }

    this.props
      .getBackupSearchResults(tag.id)
      .then(results =>
        this.setState({ backupResults: results, backupTag: tag }),
      );
  };

  render() {
    const {
      responsive,
      clip,
      sceneClips,
      loggedIn,
      ...otherProps
    } = this.props;

    let showCrumbs = false;
    if (responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT) {
      showCrumbs = true;
    }

    // Build our header crumbs list.
    let showFilterBar = false;
    let Comp = null;
    let compProps = EMPTY_OBJECT;
    let resultsMessage;
    let clearTagsCallback;
    let crumbs = [{ text: 'Browse', to: '/browse' }];

    if (clip) {
      crumbs.push({
        text: 'Scene',
        to: `/browse/scene/${clip.scene_slug}`,
      });
      crumbs.push({
        text: 'Clip',
        to: `/browse/scene/${clip.scene_slug}/${clip.clip_slug}`,
      });
      Comp = ClipLayout;
      compProps = {
        clip: clip,
        otherClipsTitle: 'Other clips from this scene',
        onBack: `/browse/scene/${clip.scene_slug}`,
        backToType: 'Scene',
        backToName: clip.scene_name,
      };
    } else if (sceneClips) {
      crumbs.push({
        text: 'Scene',
        to: `/browse/scene/${sceneClips[0].scene_slug}`,
      });

      const sceneClipIds = sceneClips.map(clip => clip.clip_id);

      let matchingClips = this.state.matchingAll
        ? []
        : this.state.allResults.result.clips || [];
      // matchingClips = intersection(matchingClips, sceneClipIds);
      matchingClips = matchingClips.filter(clip =>
        sceneClipIds.includes(clip.clip_id),
      );

      Comp = SceneLayout;
      compProps = { allClips: sceneClips, matchingClips: matchingClips };
    } else {
      showFilterBar = true;
      Comp = SceneSearchResults;
      const allResults = this.state.allResults;

      const showClipCount = !this.state.matchingAll;
      const matchingClips = get(allResults.result, 'clips', EMPTY_ARRAY);
      const backupClips = get(this.state.backupResults, 'clips', EMPTY_ARRAY);
      const matchingScenes = groupBy(matchingClips, 'scene_id');
      const sceneCount = Object.keys(matchingScenes).length;
      const isFiltered =
        !!this.props.searchText || this.props.activeTagIds.length > 0;

      if (allResults.fetching) {
        let more = sceneCount > 0 ? 'more ' : '';
        let postfix = isFiltered ? 'results ' : '';
        resultsMessage = `Loading ${more}${postfix}...`;
      } else if (showClipCount) {
        if (sceneCount > 0) {
          let prefix = '';
          if (allResults.result.found !== allResults.result.loaded) {
            prefix = 'At least ';
          }
          resultsMessage = `${prefix}${sceneCount} Scenes, ${allResults.result
            .found} Clips`;
        } else {
          resultsMessage = 'No Results';
          clearTagsCallback = this.props.clearActiveTags;
        }
      }

      compProps = {
        matchingClips: matchingClips,
        backupClips: backupClips,
        backupTitle: this.state.backupTag.name,
        fetching: allResults.fetching,
        loadMore: () => this.loadBrowseResults(true, 500),
        found: allResults.result.found,
        loaded: allResults.result.loaded,
        showClipCount: showClipCount,
        searchResultsMessage: resultsMessage,
      };
    }

    let navLinks = [
      {
        text: 'ALL SCENES',
        onClick: e => console.log(e.target),
      },
      {
        text: 'FEATURED',
        onClick: e => console.log(e.target),
      },
    ];

    if (loggedIn) {
      navLinks = navLinks.concat([
        {
          text: 'LICENSED',
          onClick: e => console.log(e.target),
        },
      ]);
    }
    // HACK: For now.
    navLinks = EMPTY_ARRAY;

    return (
      <div>
        <div
          style={{
            position: 'relative',
            zIndex: 1000,
          }}
        >
          <Header
            navLinks={navLinks}
            crumbs={crumbs}
            showFilterBar={showFilterBar}
            backgroundColor={Style.Colors.white}
            searchResultsMessage={resultsMessage}
            clearTags={clearTagsCallback}
          />
        </div>
        <Comp {...compProps} />
      </div>
    );
  }
}

function mapStateToProps(state, ownProps) {
  return {
    responsive: state.runtime.responsive,
    activeTagIds: state.search.tags,
    searchText: state.search.text,
    loggedIn: state.cognito.state === CognitoState.LOGGED_IN,
    colors: state.ui.colors,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    updateColors: colors => dispatch(setColors(colors)),
    clearColors: () => dispatch(clearColors()),
    clearActiveTags: () => dispatch(clearTags()),
    getBackupSearchResults: tagId =>
      dispatch(search(buildSearchParams('', [tagId]))),
  };
}

export default compose(
  withFooterDrawer(),
  connect(mapStateToProps, mapDispatchToProps),
)(Browse);



// WEBPACK FOOTER //
// ./src/routes/browse/Browse.js